import { useEffect, useRef, useState } from 'react';

export interface AutoFetchState<T> {
  data: T | null;
  loading: boolean;
  /** The server answered 404 — "nothing recorded yet", not a failure. */
  notFound: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * Runs `fetcher` whenever `key` changes, so a page reacts to its filters
 * without an "Apply" button. A null key means "not ready" and clears the
 * result. The last result stays on screen while the next one loads, and a
 * response that arrives after a newer request is dropped.
 */
export function useAutoFetch<T>(key: string | null, fetcher: () => Promise<T>): AutoFetchState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (key === null) {
      setData(null);
      setLoading(false);
      setNotFound(false);
      setError(null);
      return;
    }
    let stale = false;
    setLoading(true);
    setError(null);
    fetcherRef.current()
      .then((result) => {
        if (stale) return;
        setData(result);
        setNotFound(false);
      })
      .catch((err: any) => {
        if (stale) return;
        setData(null);
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          setNotFound(false);
          setError(err?.response?.data?.message || err?.message || 'Something went wrong');
        }
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });
    return () => { stale = true; };
  }, [key, attempt]);

  return { data, loading, notFound, error, retry: () => setAttempt((n) => n + 1) };
}

export default useAutoFetch;
